/**
 * Holly's bell, left in the wet grass at the end of the story.
 *
 * The prop is authored fallen rather than upright because the first view is its
 * silhouette against the field: a cup on its side, mouth toward -X, with the
 * old collar webbing dragged out behind it. `createHeldBell()` reuses the same
 * two material batches for the close shot in Rosalie's mouth; its loop top is
 * its origin, so the shot can parent it without a compensating offset.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BELL, HOLLY } from '@/render/palette';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

const MOUTH_RADIUS = 0.029;
const BELL_HEIGHT = 0.072;
const LOOP_CENTRE = 0.079;
const LOOP_TOP = 0.086;

function merged(name: string, pieces: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const geometry = mergeGeometries(pieces, false);
  if (!geometry) throw new Error(`brass-bell: ${name} merge failed`);
  return geometry;
}

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

/**
 * Break the perfectly turned outline by less than half a millimetre.
 *
 * The lip still establishes the measured 64 mm mouth and the crown/loop still
 * establish the asset height. This only catches a close light as old cast brass
 * instead of a mathematically perfect, freshly machined cup.
 */
function wobbleCup(geometry: THREE.BufferGeometry): void {
  const position = geometry.getAttribute('position');
  if (!position) throw new Error('brass-bell: cup has no positions');
  for (let i = 0; i < position.count; i += 1) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    const radius = Math.hypot(x, z);
    if (radius === 0) continue;
    const angle = Math.atan2(z, x);
    const heightFade = Math.sin(Math.PI * y / BELL_HEIGHT);
    const offset = 0.00045 * heightFade * (0.62 * Math.sin(3 * angle + 0.4) + 0.38 * Math.sin(5 * angle - 0.7));
    const scale = (radius + offset) / radius;
    position.setXYZ(i, x * scale, y, z * scale);
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
}

/** A thick-lipped cup, deliberately open at y = 0 rather than a toy bauble. */
function uprightBellGeometry(): THREE.BufferGeometry {
  const pieces: THREE.BufferGeometry[] = [];
  const cup = new THREE.LatheGeometry([
    // Outer skin: 64 mm across the lip, tapering to Holly's 38 mm crown.
    new THREE.Vector2(0.029, 0.000),
    new THREE.Vector2(0.029, 0.006),
    new THREE.Vector2(0.027, 0.020),
    new THREE.Vector2(0.024, 0.043),
    new THREE.Vector2(0.019, 0.066),
    new THREE.Vector2(0.019, BELL_HEIGHT),
    // Return down the inside. This connected wall gives the open mouth depth;
    // a single lathe skin was a brass lampshade when seen from below.
    new THREE.Vector2(0.011, 0.067),
    new THREE.Vector2(0.016, 0.059),
    new THREE.Vector2(0.021, 0.042),
    new THREE.Vector2(0.024, 0.020),
    new THREE.Vector2(0.025, 0.005),
    new THREE.Vector2(0.025, 0.000),
  ], 16);
  wobbleCup(cup);
  pieces.push(cup);

  const lip = new THREE.TorusGeometry(MOUTH_RADIUS, 0.003, 6, 16);
  lip.rotateX(Math.PI / 2);
  pieces.push(lip);

  // The loop is substantial enough to hold the strap in the final close-up;
  // a painted circle would disappear once the bell is in Rosalie's mouth.
  // Twelve major segments retain the loop's true top vertex, which is also the
  // carry-shot parent datum below. Ten leaves the nearest vertex 0.9 mm short.
  const loop = new THREE.TorusGeometry(0.005, 0.002, 5, 12);
  loop.rotateY(Math.PI / 2);
  loop.translate(0, LOOP_CENTRE, 0);
  pieces.push(loop);

  // The clapper is hung from the crown. Without this rod the ball reads as a
  // floating bead in the cavity in the ending's close shot.
  const clapperRod = new THREE.CylinderGeometry(0.002, 0.002, 0.048, 5);
  clapperRod.translate(0, 0.044, 0);
  pieces.push(clapperRod);
  const clapper = new THREE.SphereGeometry(0.008, 8, 6);
  clapper.translate(0, 0.012, 0);
  pieces.push(clapper);

  return merged('bell', pieces);
}

function fallenBellGeometry(): THREE.BufferGeometry {
  const geometry = uprightBellGeometry();
  // Upright +Y becomes +X: crown at +X and the open mouth toward -X.
  geometry.rotateZ(-Math.PI / 2);
  geometry.translate(0, MOUTH_RADIUS + 0.003, 0);
  return geometry;
}

/** A thickened flat S-ribbon with one end left in uneven fibres. */
function fallenStrapGeometry(): THREE.BufferGeometry {
  // The first 4 mm-wide folded tab actually passes through the 14 mm crown
  // loop. It opens into the 22 mm webbing after the loop, as a worn collar does.
  // Catmull-Rom samples turn the anchors into a continuous gentle S rather than
  // a chain of visible hard bends in the ending contact sheet.
  const centres = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0.079, 0.032, 0.000),
    new THREE.Vector3(0.065, 0.024, 0.016),
    new THREE.Vector3(0.032, 0.010, 0.038),
    new THREE.Vector3(-0.004, 0.004, 0.060),
    new THREE.Vector3(0.010, 0.003, 0.083),
    new THREE.Vector3(0.000, 0.003, 0.105),
  ]).getPoints(12);
  const thickness = 0.003;
  const vertices: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < centres.length; i += 1) {
    const point = centres[i];
    const previous = centres[Math.max(0, i - 1)];
    const next = centres[Math.min(centres.length - 1, i + 1)];
    if (!point || !previous || !next) throw new Error('brass-bell: strap path missing point');
    const tangent = next.clone().sub(previous).setY(0).normalize();
    const halfWidth = 0.002 + 0.009 * Math.min(1, i / 4);
    const side = new THREE.Vector3(-tangent.z, 0, tangent.x).multiplyScalar(halfWidth);
    for (const dy of [thickness / 2, -thickness / 2]) {
      for (const sign of [-1, 1]) {
        const vertex = point.clone().addScaledVector(side, sign);
        vertices.push(vertex.x, vertex.y + dy, vertex.z);
        uvs.push(sign === -1 ? 0 : 1, i / (centres.length - 1));
      }
    }
  }
  for (let i = 0; i < centres.length - 1; i += 1) {
    const a = i * 4;
    const b = a + 4;
    indices.push(a, b, a + 1, a + 1, b, b + 1, a + 2, a + 3, b + 2, a + 3, b + 3, b + 2);
    indices.push(a, a + 2, b, a + 2, b + 2, b, a + 1, b + 1, a + 3, a + 3, b + 1, b + 3);
  }
  // Close the loop end and leave the far end to the deliberately uneven fibres.
  indices.push(0, 1, 2, 1, 3, 2);
  const ribbon = new THREE.BufferGeometry();
  ribbon.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  ribbon.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  ribbon.setIndex(indices);
  ribbon.computeVertexNormals();

  const pieces: THREE.BufferGeometry[] = [ribbon];
  // Four stepped fibres span the full 22 mm webbing width. They are part of the
  // one fabric batch, but make the torn edge read from the side as well as above.
  for (const [x, z, length] of [
    [-0.008, 0.114, 0.018],
    [-0.003, 0.112, 0.028],
    [0.003, 0.114, 0.021],
    [0.008, 0.112, 0.032],
  ] as const) {
    const fibre = new THREE.BoxGeometry(0.004, thickness, length);
    fibre.rotateY((x - 0.002) * 8);
    fibre.translate(x, thickness / 2, z);
    pieces.push(fibre);
  }
  return merged('strap', pieces);
}

function buildFallenBell(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'brass-bell';
  const brass = surface('metal', BELL);
  const webbing = surface('fabric', HOLLY.collar);
  group.add(part(fallenBellGeometry(), brass, 'bell'));
  group.add(part(fallenStrapGeometry(), webbing, 'strap'));
  seatOnOrigin(group);
  return group;
}

/**
 * The carry-shot variant. The loop's highest point is exactly local origin,
 * which makes parenting to Rosalie's snout a single transform with no offset.
 */
export function createHeldBell(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'held-brass-bell';
  const brass = surface('metal', BELL);
  const webbing = surface('fabric', HOLLY.collar);
  const bell = uprightBellGeometry();
  bell.translate(0, -LOOP_TOP, 0);
  // This is exactly the grass prop's 160 mm S-ribbon, rotated back from the
  // fallen orientation and hung from the same loop. A shorter new strap made
  // the final object look like a second, unrelated bell.
  const strap = fallenStrapGeometry();
  strap.translate(0, -(MOUTH_RADIUS + 0.003), 0);
  strap.rotateZ(Math.PI / 2);
  strap.translate(0, -LOOP_TOP, 0);
  // The first transform recovers the upright bell's coordinates. Rotate that
  // same ribbon around its attachment, not around the group origin: otherwise
  // its 160 mm tail keeps lying along +Z when the bell is carried.
  strap.translate(0, LOOP_TOP - LOOP_CENTRE, 0);
  strap.rotateX(Math.PI / 2);
  strap.translate(0, LOOP_CENTRE - LOOP_TOP, 0);
  group.add(part(bell, brass, 'bell'));
  group.add(part(strap, webbing, 'strap'));
  return group;
}

export const brassBellAsset: AssetDefinition = {
  id: 'brass-bell',
  label: 'Holly\'s brass bell',
  description: 'Small fallen brass cup bell, mouth toward -X, with Holly\'s torn webbing collar.',
  // Measured from the merged fallen assembly: cup axis, lip-to-ground, then
  // cup plus the ragged tail. These are framing dimensions, not nominal ones.
  footprint: { width: 0.11, height: 0.07, length: 0.18 },
  create: buildFallenBell,
};
